import { useAlert } from 'components/alert'
import { useSession } from 'components/auth/useSession'
import { usePecField } from 'components/form/final-form/hooks/useField'
import { useUltimoExameCitopatologicoQuery } from 'graphql/hooks.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useServerTime } from 'hooks/useServerTime'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import { metaPath } from 'util/metaPath'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'

import { SolicitacaoExameModel } from '../../../model-solicitacaoExames'
import { RequisicaoExameModal, RequisicaoExameModalStep } from '../RequisicaoExameModal'
import { CitopatologicoAnamneseModalStep } from './CitopatologicoAnamneseModalStep'
import { CitopatologicoDadosColetaModalStep } from './CitopatologicoDadosColetaModalStep'
import { CitopatologicoExameClinicoModalStep } from './CitopatologicoExameClinicoModalStep'
import { CitopatologicoPerguntaField, RequisicaoExameCitopatologicoFormValues } from './model-citopatologico'
import {
  citopatologicoAnamneseValidator,
  citopatologicoDadosDaColetaValidator,
  citopatologicoExameClinicoValidator,
} from './validator-citopatologico'
interface RequisicaoExameCitopatologicoModalProps {
  onClose: () => void
  handleUpdate: (model: SolicitacaoExameModel) => void
}

const soapPath = metaPath<SoapState>()

export function RequisicaoExameCitopatologicoModal(props: Readonly<RequisicaoExameCitopatologicoModalProps>) {
  const { onClose, handleUpdate } = props
  const alert = useAlert()

  const { data: session } = useSession()

  const { getServerTimeNow } = useServerTime()
  const serverTime = getServerTimeNow()

  const { cidadao, prontuario } = useAtendimentoContext()
  const dataNascimentoCidadao = cidadao.dataNascimento

  const profissional = session?.profissional

  const { id } = useParams<{ id: string }>()

  const { data: ultimoExameData } = useUltimoExameCitopatologicoQuery({ variables: { input: prontuario.id } })
  const ultimoExame = ultimoExameData?.ultimaSolicitacaoCitopatologicoUtero

  const steps: RequisicaoExameModalStep<RequisicaoExameCitopatologicoFormValues>[] = useMemo(
    () => [
      {
        title: 'Anamnese',
        render: () => <CitopatologicoAnamneseModalStep cidadao={cidadao} serverTime={serverTime} />,
        validator: citopatologicoAnamneseValidator(dataNascimentoCidadao!, ultimoExame),
      },
      {
        title: 'Exame clínico',
        render: () => <CitopatologicoExameClinicoModalStep />,
        validator: citopatologicoExameClinicoValidator,
      },
      {
        title: 'Dados da coleta',
        render: () => <CitopatologicoDadosColetaModalStep cidadao={cidadao} serverTime={serverTime} />,
        validator: citopatologicoDadosDaColetaValidator(dataNascimentoCidadao!),
      },
    ],
    [cidadao, dataNascimentoCidadao, serverTime, ultimoExame]
  )

  const {
    input: { value: adicionados },
  } = usePecField<ReadonlyArray<SolicitacaoExameModel>>({
    name: soapPath.plano.solicitacoesExame,
    subscription: { value: true },
  })

  const solicitacao = adicionados?.find((item: SolicitacaoExameModel) => item._id === id)

  const handleSubmit = (values: RequisicaoExameCitopatologicoFormValues) => {
    if (solicitacao) {
      handleUpdate({
        ...solicitacao,
        rastreioCitopatologicoColoUtero: values,
      })
    }
    alert('success', 'Requisição de exame citopatológico de colo uterino salva com sucesso')
    onClose()
  }

  const initialValues = {
    [CitopatologicoPerguntaField.nomeResponsavel]: profissional?.nome ?? undefined,
    [CitopatologicoPerguntaField.cns]: profissional?.cns ?? undefined,
    [CitopatologicoPerguntaField.numeroConselho]: profissional?.numeroConselhoClasse ?? undefined,
    [CitopatologicoPerguntaField.siglaNomeConselho]: profissional?.conselhoClasse?.sigla ?? undefined,
    ...solicitacao?.rastreioCitopatologicoColoUtero,
  }

  return (
    <RequisicaoExameModal<RequisicaoExameCitopatologicoFormValues>
      title='Requisição de exame citopatológico de colo uterino'
      steps={steps}
      onSubmit={handleSubmit}
      onClose={onClose}
      initialValues={initialValues}
    />
  )
}
